import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch, apiFetchBlob, apiUpload } from '@/lib/api/rawFetch'

// Interfaces declared locally until the next `npm run gen-api` refresh
// (same escape hatch as useEvents.ts) — mirror backend/schemas/product_requests.py.

export type RequestStatus =
  | 'OPEN'
  | 'NEED_TO_SOURCE'
  | 'SOURCED'
  | 'ADDED_TO_PO'
  | 'ORDERED'
  | 'CUSTOMER_NOTIFIED'
  | 'PURCHASED'
  | 'CLOSED_NO_PURCHASE'

export type RequestOrigin = 'BREWSTER' | 'PHONE_EMAIL' | 'EVENT'

export type RequestUrgency = 'STANDARD' | 'URGENT'

export type BuyingDecision = 'TBD' | 'WILL_CARRY' | 'WILL_NOT_CARRY'

export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  OPEN: 'Open',
  NEED_TO_SOURCE: 'Need to Source',
  SOURCED: 'Sourced',
  ADDED_TO_PO: 'Added to PO',
  ORDERED: 'Ordered',
  CUSTOMER_NOTIFIED: 'Customer Notified',
  PURCHASED: 'Customer Purchased',
  CLOSED_NO_PURCHASE: 'Closed — No Purchase',
}

export const ORIGIN_LABELS: Record<RequestOrigin, string> = {
  BREWSTER: 'Brewster Store',
  PHONE_EMAIL: 'Phone / Email',
  EVENT: 'Event',
}

/**
 * Everything that isn't a terminal outcome. Drives the "still needs work"
 * count in the nav badge and the overdue follow-up highlight — a follow-up
 * date in the past doesn't matter once the request is resolved.
 */
export const OPEN_STATUSES: RequestStatus[] = [
  'OPEN',
  'NEED_TO_SOURCE',
  'SOURCED',
  'ADDED_TO_PO',
  'ORDERED',
  'CUSTOMER_NOTIFIED',
]

export interface ProductRequest {
  id: number
  customer_name: string
  customer_email: string | null
  customer_phone: string | null
  shopify_customer_id: string | null
  shopify_customer_url: string | null
  variant_id: number | null
  product_id: number | null
  product_title: string
  /** Shopify vendor of the linked variant, from the dashboard cache. Null for
   *  a manually-described product (or a cold cache) — `vendor` is the typed
   *  sourcing note and is a separate field. */
  product_vendor: string | null
  variant_title: string | null
  sku: string | null
  brand: string | null
  product_type: string | null
  size: string | null
  color: string | null
  material: string | null
  approx_price: number | null
  reference_url: string | null
  qty: number
  needed_by: string | null
  originated_from: RequestOrigin
  event_location_id: number | null
  event_location_name: string | null
  urgency: RequestUrgency
  assigned_to: string | null
  follow_up_date: string | null
  status: RequestStatus
  vendor: string | null
  est_cost: number | null
  buying_decision: BuyingDecision | null
  notes: string | null
  po_id: string | null
  po_variant_id: number | null
  email1_sent_at: string | null
  email2_sent_at: string | null
  resolved_at: string | null
  photo_purged_at: string | null
  legacy_event_request_id: number | null
  created_by: string | null
  created_at: string | null
  updated_at: string | null
  demand_count: number
  has_photo: boolean
  on_hand: number | null
  po_status: string | null
  /** Set when the request was logged alongside siblings in one submission. */
  request_group_id: string | null
  /** Rows sharing that group id; 0 when the request isn't grouped. */
  group_count: number
}

export interface ProductRequestFilters {
  status?: RequestStatus
  assigned_to?: string
  originated_from?: RequestOrigin
  date_from?: string
  date_to?: string
  search?: string
  /** Every request pinned to this Shopify variant, any status. */
  variant_id?: number
}

/** All 8 status keys are always present in the response. */
export type RequestCounts = Record<string, number>

export function useProductRequests(filters?: ProductRequestFilters) {
  return useQuery({
    queryKey: ['product-requests', filters ?? {}] as const,
    queryFn: () =>
      apiFetch<ProductRequest[]>('GET', '/api/product-requests', {
        query: {
          status: filters?.status,
          assigned_to: filters?.assigned_to,
          originated_from: filters?.originated_from,
          date_from: filters?.date_from,
          date_to: filters?.date_to,
          search: filters?.search,
          variant_id: filters?.variant_id,
        },
      }),
    staleTime: 30_000,
  })
}

export function useRequestCounts() {
  return useQuery({
    queryKey: ['product-request-counts'] as const,
    queryFn: () => apiFetch<RequestCounts>('GET', '/api/product-requests/counts'),
    staleTime: 60_000,
  })
}

export function useChangeRequestStatus() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      status,
      send_email,
    }: {
      id: number
      status: RequestStatus
      send_email?: boolean
    }) =>
      apiFetch<{ id: number; status: RequestStatus; email_sent: boolean }>(
        'POST',
        `/api/product-requests/${id}/status`,
        { body: { status, send_email } }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-requests'] })
      qc.invalidateQueries({ queryKey: ['product-request-counts'] })
    },
  })
}

export function useDeleteRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<{ status: string; request_id: number }>(
        'DELETE',
        `/api/product-requests/${id}`
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-requests'] })
      qc.invalidateQueries({ queryKey: ['product-request-counts'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Create / update
// ---------------------------------------------------------------------------

/**
 * The PATCH wire fields, mirroring backend ProductRequestUpdate. Every field
 * is sent as its natural type (never null): the backend reads `None` as
 * "leave unchanged", so a field is cleared by sending an empty string, and
 * omitted entirely when it shouldn't move. Callers always pass a
 * `Partial<>` of this.
 *
 * Consequence worth knowing: `variant_id` / `product_id` can't be *cleared*
 * through PATCH — converting a Shopify-linked request back to a
 * manually-described one isn't expressible on this endpoint.
 */
export interface ProductRequestUpdatePayload {
  customer_name: string
  customer_email: string
  customer_phone: string
  shopify_customer_id: string
  shopify_customer_url: string
  variant_id: number
  product_id: number
  product_title: string
  variant_title: string
  sku: string
  brand: string
  product_type: string
  size: string
  color: string
  material: string
  approx_price: number
  reference_url: string
  qty: number
  needed_by: string
  originated_from: RequestOrigin
  event_location_id: number
  event_location_name: string
  urgency: RequestUrgency
  assigned_to: string
  follow_up_date: string
  vendor: string
  est_cost: number
  buying_decision: BuyingDecision
  notes: string
}

/** POST body — the same field set, with the two the backend requires, plus
 *  the initial status (only create may set it directly). */
export interface ProductRequestCreatePayload
  extends Partial<ProductRequestUpdatePayload> {
  customer_name: string
  originated_from: RequestOrigin
  status?: RequestStatus
}

export function useCreateRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ProductRequestCreatePayload) =>
      apiFetch<ProductRequest>('POST', '/api/product-requests', { body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-requests'] })
      qc.invalidateQueries({ queryKey: ['product-request-counts'] })
    },
  })
}

/**
 * One product on a batch create. Same product columns as the single POST,
 * minus everything the siblings share (customer, context, assignment) — those
 * live once at the top level of the batch body.
 */
export interface BatchProductEntry {
  variant_id?: number
  product_id?: number
  product_title?: string
  variant_title?: string
  sku?: string
  brand?: string
  product_type?: string
  size?: string
  color?: string
  material?: string
  approx_price?: number
  reference_url?: string
  vendor?: string
  est_cost?: number
  buying_decision?: BuyingDecision
  qty: number
}

/** POST /batch body — the shared fields once, then 1..N products. */
export interface ProductRequestBatchPayload
  extends Partial<ProductRequestUpdatePayload> {
  customer_name: string
  originated_from: RequestOrigin
  status?: RequestStatus
  products: BatchProductEntry[]
}

/**
 * The created siblings, in the same order as `products`. `request_group_id`
 * is null for a single-product batch — one request isn't a group.
 */
export interface ProductRequestBatchResult {
  requests: ProductRequest[]
  request_group_id: string | null
}

export function useCreateRequestBatch() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ProductRequestBatchPayload) =>
      apiFetch<ProductRequestBatchResult>('POST', '/api/product-requests/batch', {
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-requests'] })
      qc.invalidateQueries({ queryKey: ['product-request-counts'] })
    },
  })
}

export function useUpdateRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: number
      patch: Partial<ProductRequestUpdatePayload>
    }) =>
      apiFetch<ProductRequest>('PATCH', `/api/product-requests/${id}`, {
        body: patch,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-requests'] })
      qc.invalidateQueries({ queryKey: ['product-request-counts'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Customer lookup
// ---------------------------------------------------------------------------

export interface ShopifyCustomerInfo {
  shopify_customer_id: string
  admin_url: string
  display_name: string
  email: string
  phone: string | null
  orders_count: number
  /** Dollars straight off Shopify's amountSpent — not our cents convention. */
  total_spent: number
  total_spent_currency: string
  last_order_at: string | null
}

export interface CustomerLookup {
  found: boolean
  customer?: ShopifyCustomerInfo
}

/**
 * Plain async call rather than a query hook: the lookup fires on a field
 * blur, and the backend always answers 200 (`found:false` covers no match,
 * missing creds and upstream failure alike), so there's nothing to cache or
 * retry — staff must never see an error for typing an unknown address.
 */
export async function lookupCustomer(email: string): Promise<CustomerLookup> {
  return apiFetch<CustomerLookup>('GET', '/api/product-requests/customer-lookup', {
    query: { email },
  })
}

/**
 * Typeahead behind the three customer fields on the log-request form. Same
 * contract as the lookup: always 200, so a short query, absent Shopify creds
 * or an upstream failure all read as "no matches" rather than an error the
 * staff member has to dismiss mid-typing.
 */
export async function searchCustomers(
  q: string,
  limit = 8
): Promise<ShopifyCustomerInfo[]> {
  const res = await apiFetch<{ results: ShopifyCustomerInfo[] }>(
    'GET',
    '/api/product-requests/customer-search',
    { query: { q, limit } }
  )
  return res.results ?? []
}

// ---------------------------------------------------------------------------
// Demand summary
// ---------------------------------------------------------------------------

export interface DemandProduct {
  product_key: string
  product_title: string
  request_count: number
  total_qty: number
}

export interface OverdueFollowUp {
  id: number
  customer_name: string
  product_title: string
  qty: number
  assigned_to: string | null
  follow_up_date: string
  status: string
}

export interface ConversionStats {
  created_90d: number
  purchased_90d: number
  rate_pct: number
}

/** A free-form dimension (product type, brand) with how many requests carry it. */
export interface DemandBreakdown {
  label: string
  count: number
}

export interface DemandSummary {
  top_products: DemandProduct[]
  counts_by_status: Record<string, number>
  counts_by_source: Record<string, number>
  /** Sorted count-desc by the backend. Absent on a response cached before
   *  these were added, so callers default to []. */
  by_product_type: DemandBreakdown[]
  by_brand: DemandBreakdown[]
  overdue_follow_ups: OverdueFollowUp[]
  conversion: ConversionStats
}

/** Only fetched while the summary modal is open — it's four aggregate
 *  queries, not something the board needs in the background. */
export function useDemandSummary(enabled: boolean) {
  return useQuery({
    queryKey: ['product-request-demand-summary'] as const,
    queryFn: () =>
      apiFetch<DemandSummary>('GET', '/api/product-requests/demand-summary'),
    enabled,
    staleTime: 60_000,
  })
}

// ---------------------------------------------------------------------------
// Add to PO
// ---------------------------------------------------------------------------

export interface AddToPOVariantPayload {
  variant_id: number
  product_id?: number
  product_title: string
  variant_title?: string
  sku?: string
  barcode?: string
  retail_price?: number
  cost_base?: number
  vendor?: string
}

export interface AddToPONewPOPayload {
  vendor: string
  supplier?: string
  location_to_receive?: string
}

/** Exactly one of `po_id` / `new_po` — the backend rejects both or neither. */
export interface AddToPOPayload {
  po_id?: string
  new_po?: AddToPONewPOPayload
  variant?: AddToPOVariantPayload
  qty: number
}

export interface AddToPOResult {
  request: ProductRequest
  po_id: string
  po_status: string
  /** qty_ordered on the PO line AFTER the merge, not just what we added. */
  qty_on_line: number
}

export function useAddToPO() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: AddToPOPayload }) =>
      apiFetch<AddToPOResult>('POST', `/api/product-requests/${id}/add-to-po`, {
        body,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['product-requests'] })
      qc.invalidateQueries({ queryKey: ['product-request-counts'] })
      // The PO gained a line (or was created outright) — the PO board and its
      // budget are now stale too.
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
    },
  })
}

// ---------------------------------------------------------------------------
// Link to an existing (past-draft) PO line
// ---------------------------------------------------------------------------

/** Attaches a waiting request to a line that's already on the PO — the order
 *  itself (item / qty / cost) is untouched. */
export interface LinkToPOLinePayload {
  po_id: string
  send_ordered_email: boolean
}

export interface LinkToPOLineResult {
  request: ProductRequest
  po_id: string
  qty_on_line: number
  /** Units on the line now promised to requests, including this one. */
  spoken_for: number
  /** Units this request took — below its qty when it was split. */
  linked_qty: number
  /** The new request holding the units that didn't fit; null when no split. */
  split_request_id: number | null
}

export function useLinkRequestToPOLine() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: LinkToPOLinePayload }) =>
      apiFetch<LinkToPOLineResult>(
        'POST',
        `/api/product-requests/${id}/link-to-po-line`,
        { body }
      ),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['product-requests'] })
      qc.invalidateQueries({ queryKey: ['product-request-counts'] })
      qc.invalidateQueries({ queryKey: ['pos'] })
      qc.invalidateQueries({ queryKey: ['po-budget'] })
      // The backend rewrites the line's "Requested by" note — refetch the
      // expanded PO so it shows.
      qc.invalidateQueries({ queryKey: ['po', res.po_id] })
    },
  })
}

// ---------------------------------------------------------------------------
// Reference photo
//
// One photo per request. Upload/delete are plain calls rather than mutations:
// they're always chained onto a create/update the modal has already awaited,
// and the list invalidation comes from that mutation.
// ---------------------------------------------------------------------------

export function uploadRequestPhoto(id: number, file: File) {
  return apiUpload<{ ok: boolean; size: number }>(
    `/api/product-requests/${id}/photo`,
    file
  )
}

export function deleteRequestPhoto(id: number) {
  return apiFetch<{ ok: boolean }>('DELETE', `/api/product-requests/${id}/photo`)
}

export interface RequestPhoto {
  url: string
  /** The blob's own MIME type — a download needs it to name the file. */
  mime: string
}

/**
 * The photo endpoint is auth-gated, so an <img src> pointing at it would 401.
 * Fetch it as a blob and hand back an object URL — the caller owns revoking it.
 */
export async function fetchRequestPhoto(id: number): Promise<RequestPhoto> {
  const blob = await apiFetchBlob('GET', `/api/product-requests/${id}/photo`)
  return { url: URL.createObjectURL(blob), mime: blob.type }
}

/** URL-only form, for callers that just need a preview src. */
export async function fetchRequestPhotoUrl(id: number): Promise<string> {
  return (await fetchRequestPhoto(id)).url
}
